import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listPublicOfferings,
  type OfferingCard,
  type OfferingDisclosures as Disclosures,
} from '../../lib/offerings';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatMoneyCompact } from '../../lib/format';
import { OfferingLegends } from '../../components/OfferingDisclosures';
import './InvestPage.css';

const SECURITY_LABEL: Record<string, string> = {
  equity_units: 'Equity units',
  revenue_share: 'Revenue share',
  convertible_note: 'Convertible note',
  safe: 'SAFE',
};

/**
 * A deal card.
 *
 * Shows only what a 506(c) teaser may show: the pitch, the SIZE of the raise,
 * the minimum ticket, the security type and the closing date. There is
 * deliberately no progress bar and no "X% funded" — the amount raised so far is
 * not public information, and the engine does not send it, so there is nothing
 * here to accidentally render.
 *
 * The call to action is "Apply to Invest", never "Invest Now". Nothing on this
 * surface can execute: applying opens a screener, and the most that happens is
 * a human gets in touch.
 */
function DealCard({ offering }: { offering: OfferingCard }) {
  const where = [offering.event.venueName, offering.event.venueCity].filter(Boolean).join(' · ');

  return (
    <article className="deal-card">
      <div className="deal-card__media">
        {offering.event.featuredImageUrl ? (
          <img src={offering.event.featuredImageUrl} alt="" loading="lazy" />
        ) : (
          <div className="deal-card__media-fallback" aria-hidden="true">
            <span className="display">{offering.title.slice(0, 1)}</span>
          </div>
        )}
        <span className="deal-card__reg">Reg D · Rule 506(c)</span>
      </div>

      <div className="deal-card__body">
        <p className="deal-card__event mono">
          {formatDateShort(offering.event.eventDate)}
          {where ? ` · ${where}` : ''}
        </p>
        <h3 className="deal-card__title">{offering.title}</h3>
        {offering.description && (
          <p className="deal-card__pitch">{offering.description}</p>
        )}

        <dl className="deal-card__terms">
          {/* OMITTED, not blanked, for an unapproved viewer. formatMoneyCompact turns
              undefined into a dash, so rendering these unconditionally would print
              "Target raise -" and tell a visitor a figure exists that they are not being
              shown. The row does not appear at all. */}
          {offering.financialsVisible && (
            <>
              <div className="deal-card__term">
                <dt>Target raise</dt>
                <dd className="mono">{formatMoneyCompact(offering.targetRaiseCents)}</dd>
              </div>
              <div className="deal-card__term">
                <dt>Minimum</dt>
                <dd className="mono">{formatMoneyCompact(offering.minimumInvestmentCents)}</dd>
              </div>
            </>
          )}
          <div className="deal-card__term">
            <dt>Security</dt>
            <dd>{SECURITY_LABEL[offering.securityType] ?? offering.securityType}</dd>
          </div>
          <div className="deal-card__term">
            <dt>Closes</dt>
            <dd className="mono">
              {offering.closesAt ? formatDateShort(offering.closesAt) : 'TBA'}
            </dd>
          </div>
        </dl>

        <div className="deal-card__footer">
          {offering.daysRemaining != null && (
            <span className="deal-card__days mono">
              {offering.daysRemaining === 0
                ? 'Closing today'
                : `${offering.daysRemaining} days left`}
            </span>
          )}
          <Link to={`/invest/${offering.id}`} className="button button--primary">
            View offering
          </Link>
        </div>
      </div>
    </article>
  );
}

export function InvestPage() {
  const [offerings, setOfferings] = useState<OfferingCard[]>([]);
  const [disclosures, setDisclosures] = useState<Disclosures | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPublicOfferings()
      .then((result) => {
        if (cancelled) return;
        setOfferings(result.offerings);
        setDisclosures(result.disclosures);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load offerings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="invest-page" data-surface="invest">
      <header className="invest-hero">
        <p className="invest-hero__eyebrow mono">Reg D · Rule 506(c)</p>
        <h1 className="invest-hero__title display">Investment Opportunities</h1>
        {/* Names the producing and managing entity rather than Voleto. Voleto operates the
            platform; it does not raise capital, produce, or manage anything listed here,
            and the closing sentence says whose offer this is not. */}
        <p className="invest-hero__lede">
          Live entertainment offerings produced and managed by Vyrtual LLC. Every offering
          below is open to applications from accredited investors; terms shown reflect the
          structure of the deal and are not an offer to sell by Voleto.
        </p>
      </header>

      {loading && <p className="invest-state">Loading offerings…</p>}

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && offerings.length === 0 && (
        <div className="invest-empty">
          <h2>No open offerings right now</h2>
          <p>
            Productions are listed here while they are raising. Check back, or apply to
            hear about the next one.
          </p>
          <Link to="/invest/interest" className="button button--primary">
            Register interest
          </Link>
        </div>
      )}

      {offerings.length > 0 && (
        <div className="invest-grid">
          {offerings.map((offering) => (
            <DealCard key={offering.id} offering={offering} />
          ))}
        </div>
      )}

      {/* Figures appear on every card above, so the legends belong on this page  | 
          not only on the detail view. */}
      {disclosures && <OfferingLegends disclosures={disclosures} />}
    </div>
  );
}
